import React, { useState } from "react";
import { api } from "@/lib/api";
import { Section } from "@/components/Section";
import { toast } from "sonner";

const WEIGHTS = ["Flyweight","Bantamweight","Featherweight","Lightweight","Welterweight","Middleweight","Light Heavyweight","Heavyweight"];

export default function Register() {
  const [f, setF] = useState({
    full_name:"", dob:"", phone:"", email:"", city:"", state:"",
    sport:"MMA", weight_class:"Lightweight", level:"Amateur",
    fight_record:"", academy:"", coach:"", experience:"",
    photo_path:"", document_path:"",
    consent_declaration:false, consent_privacy:false,
  });
  const [photo, setPhoto] = useState(null);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const uploadFile = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    const { data } = await api.post("/uploads/public", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.path;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent_declaration || !f.consent_privacy) { toast.error("Please accept the declaration and privacy consent."); return; }
    setLoading(true);
    try {
      let photo_path = "", document_path = "";
      if (photo) photo_path = await uploadFile(photo, "image");
      if (doc)   document_path = await uploadFile(doc, "document");
      await api.post("/registrations", { ...f, photo_path, document_path });
      setOk(true);
      toast.success("Registration received");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setLoading(false); }
  };

  if (ok) return (
    <div className="min-h-[70vh] flex items-center" data-testid="register-success">
      <div className="max-w-2xl mx-auto text-center px-5 py-24">
        <div className="type-label text-[#C8102E] mb-3">Received</div>
        <h1 className="type-h2">Application Submitted</h1>
        <div className="divider-red mx-auto my-6" />
        <p className="text-[#c9c9c9] leading-relaxed">
          Thank you. Your registration has been received. <br />
          <strong className="text-white">Submitting your details does not confirm participation.</strong> The Ironveil Era team will review your application and respond to <span className="text-white">{f.email}</span>.
        </p>
      </div>
    </div>
  );

  return (
    <div data-testid="register-page">
      <div className="border-b border-[#151515] pt-24 pb-16 bg-black">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="type-label text-[#C8102E] mb-4">Athlete Registration</div>
          <h1 className="type-h1">Enter the Era</h1>
          <p className="mt-6 text-[#a0a0a0] max-w-xl">Submit your athlete profile. Our team will review and respond.</p>
        </div>
      </div>

      <Section>
        <form onSubmit={submit} className="grid md:grid-cols-2 gap-5 max-w-4xl" data-testid="register-form">
          <Input label="Full Name" value={f.full_name} onChange={(v) => set("full_name", v)} required testid="r-name" />
          <Input label="Date of Birth" type="date" value={f.dob} onChange={(v) => set("dob", v)} required testid="r-dob" />
          <Input label="Phone" value={f.phone} onChange={(v) => set("phone", v)} required testid="r-phone" />
          <Input label="Email" type="email" value={f.email} onChange={(v) => set("email", v)} required testid="r-email" />
          <Input label="City" value={f.city} onChange={(v) => set("city", v)} required testid="r-city" />
          <Input label="State" value={f.state} onChange={(v) => set("state", v)} required testid="r-state" />
          <Select label="Sport" value={f.sport} onChange={(v) => set("sport", v)} options={["MMA","Boxing"]} testid="r-sport" />
          <Select label="Competition Level" value={f.level} onChange={(v) => set("level", v)} options={["Amateur","Pro"]} testid="r-level" />
          <Select label="Weight Class" value={f.weight_class} onChange={(v) => set("weight_class", v)} options={WEIGHTS} testid="r-weight" />
          <Input label="Fight Record (e.g. 3-1-0)" value={f.fight_record} onChange={(v) => set("fight_record", v)} testid="r-record" />
          <Input label="Academy / Gym" value={f.academy} onChange={(v) => set("academy", v)} testid="r-academy" />
          <Input label="Coach" value={f.coach} onChange={(v) => set("coach", v)} testid="r-coach" />
          <div className="md:col-span-2">
            <Field label="Previous Competition Experience">
              <textarea rows={4} value={f.experience} onChange={(e) => set("experience", e.target.value)} className="w-full px-4 py-3" data-testid="r-experience" />
            </Field>
          </div>
          <Field label="Athlete Photo (JPG/PNG/WebP, max 8MB)">
            <input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files?.[0] || null)} className="w-full px-4 py-3" data-testid="r-photo" />
          </Field>
          <Field label="Supporting Document (optional, PDF/Image)">
            <input type="file" accept="image/*,application/pdf" onChange={(e) => setDoc(e.target.files?.[0] || null)} className="w-full px-4 py-3" data-testid="r-doc" />
          </Field>

          <div className="md:col-span-2 space-y-3">
            <label className="flex items-start gap-3 text-sm text-[#c9c9c9]">
              <input type="checkbox" checked={f.consent_declaration} onChange={(e) => set("consent_declaration", e.target.checked)} className="mt-1" data-testid="r-consent-decl" />
              <span>I declare the above information is accurate. I understand that submitting this form does not confirm participation.</span>
            </label>
            <label className="flex items-start gap-3 text-sm text-[#c9c9c9]">
              <input type="checkbox" checked={f.consent_privacy} onChange={(e) => set("consent_privacy", e.target.checked)} className="mt-1" data-testid="r-consent-priv" />
              <span>I agree to the Ironveil Era Privacy Policy and processing of my athlete details.</span>
            </label>
          </div>

          <div className="md:col-span-2">
            <button disabled={loading} className="btn-primary" data-testid="r-submit">{loading ? "Submitting…" : "Submit Registration"}</button>
          </div>
        </form>
      </Section>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><div className="type-meta text-[#888] mb-2">{label}</div>{children}</label>;
}
function Input({ label, value, onChange, required, type = "text", testid }) {
  return <Field label={label}><input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} className="w-full px-4 py-3" data-testid={testid} /></Field>;
}
function Select({ label, value, onChange, options, testid }) {
  return <Field label={label}><select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-4 py-3" data-testid={testid}>{options.map((o) => <option key={o} value={o}>{o}</option>)}</select></Field>;
}
